import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import VideoDialog from "@/components/VideoDialog";
import { Dumbbell, Zap, Target, Activity, PlayCircle } from "lucide-react";

const EQUIPMENT = [
  { key: "all", label: "TUTTI", icon: Target },
  { key: "dumbbells", label: "MANUBRI", icon: Dumbbell },
  { key: "kettlebell", label: "KETTLEBELL", icon: Zap },
  { key: "bands", label: "ELASTICI", icon: Activity },
  { key: "bodyweight", label: "CORPO LIBERO", icon: Target },
];

const MUSCLES = [
  { key: "all", label: "TUTTI" },
  { key: "legs", label: "GAMBE" },
  { key: "cardio", label: "CARDIO" },
  { key: "core", label: "CORE" },
  { key: "upper", label: "PARTE ALTA" },
  { key: "full_body", label: "FULL BODY" },
];

export default function Library() {
  const [items, setItems] = useState([]);
  const [equip, setEquip] = useState("all");
  const [muscle, setMuscle] = useState("all");
  const [loading, setLoading] = useState(true);
  const [videoEx, setVideoEx] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get("/exercises", { params: { equipment: equip, muscle_group: muscle } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [equip, muscle]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-2">Arsenale</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Libreria <span className="text-[#FF3B30]">esercizi</span>
      </h1>

      <div className="space-y-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Attrezzatura</div>
          <div className="flex flex-wrap gap-2">
            {EQUIPMENT.map((e) => (
              <button
                key={e.key}
                onClick={() => setEquip(e.key)}
                data-testid={`filter-equip-${e.key}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-all flex items-center gap-2 ${
                  equip === e.key
                    ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                    : "border-[#2A2A35] text-[#A0A0AB] hover:border-white hover:text-white"
                }`}
              >
                <e.icon className="w-3 h-3" />
                {e.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Gruppo muscolare</div>
          <div className="flex flex-wrap gap-2">
            {MUSCLES.map((m) => (
              <button
                key={m.key}
                onClick={() => setMuscle(m.key)}
                data-testid={`filter-muscle-${m.key}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-all ${
                  muscle === m.key
                    ? "bg-[#E2FF31] border-[#E2FF31] text-black"
                    : "border-[#2A2A35] text-[#A0A0AB] hover:border-white hover:text-white"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#A0A0AB] font-display uppercase tracking-widest text-sm">Caricamento...</div>
      ) : items.length === 0 ? (
        <div className="border border-[#2A2A35] p-8 text-center text-[#A0A0AB]">Nessun esercizio trovato con questi filtri.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger">
          {items.map((ex) => (
            <div
              key={ex.id}
              data-testid={`ex-${ex.id}`}
              className="border border-[#2A2A35] p-5 hover:border-[#FF3B30] hover:-translate-y-1 transition-all"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB] mb-1">
                    {ex.equipment} · {ex.muscle_group}
                  </div>
                  <div className="font-display text-2xl font-bold uppercase leading-tight">{ex.name}</div>
                </div>
                <span
                  className={`text-[9px] uppercase tracking-widest px-2 py-1 border ${
                    ex.difficulty === "facile"
                      ? "border-[#22C55E] text-[#22C55E]"
                      : ex.difficulty === "medio"
                      ? "border-[#F59E0B] text-[#F59E0B]"
                      : "border-[#EF4444] text-[#EF4444]"
                  }`}
                >
                  {ex.difficulty}
                </span>
              </div>
              <p className="text-sm text-[#A0A0AB] leading-relaxed mb-3">{ex.description}</p>
              <button
                onClick={() => setVideoEx(ex)}
                data-testid={`video-${ex.id}`}
                className="w-full mb-3 border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] text-xs font-bold uppercase tracking-widest py-2.5 flex items-center justify-center gap-2 transition-all group/vid"
              >
                <PlayCircle className="w-4 h-4 group-hover/vid:fill-[#E2FF31]/20" strokeWidth={2.5} />
                Guarda video demo
              </button>
              <div className="text-xs text-white/80 border-l-2 border-[#E2FF31] pl-3 mb-3">
                <span className="text-[9px] uppercase tracking-widest text-[#E2FF31]">Tip · </span>{ex.tips}
              </div>
              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#2A2A35]">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB]">Serie</div>
                  <div className="font-display text-2xl font-black">{ex.default_sets}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB]">Reps</div>
                  <div className="font-display text-2xl font-black">{ex.default_reps}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A0AB]">Recupero</div>
                  <div className="font-display text-2xl font-black">{ex.rest_seconds}s</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <VideoDialog exercise={videoEx} onClose={() => setVideoEx(null)} />
    </div>
  );
}
